import { Accordion, Box, Heading, Image, Layout, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import ChooseARenterImage from '../../../images/rentalProtection/choose_a_renter.svg';
import AddALeaseImage from '../../../images/rentalProtection/add_a_lease.svg';
import SetupPaymentsImage from '../../../images/rentalProtection/setup_payments.svg';
import LeaseQualificationAccordion from '../../rentGuarantee/components/LeaseQualificationAccordion';

import { gridContentStyles } from './styles';

type RentalProtectionHowDoesItWorkModalBodyProps = {
  onDropRentalProtectionClick: (() => void) | null;
  isFromModularListingFlow?: boolean;
  isLowFrictionEnabled?: boolean;
};

const RentalProtectionHowDoesItWorkModalBody = ({
  onDropRentalProtectionClick = null,
  isFromModularListingFlow = false,
  isLowFrictionEnabled = false,
}: RentalProtectionHowDoesItWorkModalBodyProps) => {
  const renderAccordion = (isLowFrictionEnabled: boolean) => {
    if (isLowFrictionEnabled) {
      return (
        <Accordion.Root css={{ marginBlock: '800' }}>
          <LeaseQualificationAccordion />
        </Accordion.Root>
      );
    }

    return (
      <Accordion.Root css={{ marginBlock: '800' }}>
        <Accordion.Item
          value="rental-protection-z-tools-requirements"
          data-testid="RentalProtectionHowDoesItWorkZToolsRequirementsAccordion"
        >
          <Accordion.Header>
            Why does Rental Protection require the use of Zillow Rental Manager tools?
          </Accordion.Header>
          <Accordion.Panel>
            <Paragraph css={{ marginBottom: '400' }}>
              Zillow Insurance Services uses information it receives from Zillow Rental Manager to prepare an insurance
              request on your behalf for Rental Protection coverage.
            </Paragraph>
            <Paragraph css={{ marginBottom: '400' }}>
              Zillow Insurance Services also sends rental payment information to LeaseLock that can be used to verify a
              claim for renter default.
            </Paragraph>
            <Paragraph>Here&apos;s how the Zillow Rental Manager tools help with Rental Protection:</Paragraph>
            <List.Root>
              <List.Item css={{ paddingBlock: '0' }}>
                <Text textStyle="body-bold">Applications.</Text> Applicants can upload income documents to help you
                verify tenants have the minimum income-to-rent ratio required for Rental Protection.
              </List.Item>
              <List.Item css={{ paddingBlock: '0' }}>
                <Text textStyle="body-bold">Leases.</Text> Zillow Insurance Services can quickly send your lease terms
                to LeaseLock.
              </List.Item>
              <List.Item css={{ paddingBlock: '0' }}>
                <Text textStyle="body-bold">Payments.</Text> The record of payment transactions between you and the
                renter helps track if rent payments are missed, so Zillow Insurance Services can provide that
                information to LeaseLock for claims processing.
              </List.Item>
            </List.Root>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    );
  };

  return (
    <div data-testid="RentalProtectionHowDoesItWorkModalBody">
      <Heading
        level={1}
        textStyle="heading-sm"
        fontFamily="heading-alt"
        color={isFromModularListingFlow ? 'text.accent.blue.hero' : 'text.neutral'}
        css={{ marginBottom: '400', marginTop: '200' }}
      >
        How to purchase Rental Protection
      </Heading>
      <Paragraph css={{ marginBottom: '400' }}>
        Rental Protection requires use of Zillow&apos;s free tools. Any documents you collect are all in one place,
        which saves you time if you need to file a claim.
      </Paragraph>
      <Layout.Root css={{ gap: '400' }}>
        {isLowFrictionEnabled ? (
          <>
            <Layout.Content
              css={gridContentStyles}
              data-testid="RentalProtectionHowDoesItWorkModalBodyItem-LowFriction"
            >
              <Image src={ChooseARenterImage} alt="" css={{ width: '190px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 1
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Upload or create a Zillow Lease
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Upload your own 6 - 24 month lease or use our template to create a new one.
              </Paragraph>
            </Layout.Content>
            <Layout.Content
              css={gridContentStyles}
              data-testid="RentalProtectionHowDoesItWorkModalBodyItem-LowFriction"
            >
              <Image src={AddALeaseImage} alt="" css={{ width: '150px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 2
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Confirm tenant eligibility
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Confirm that your tenant&apos;s household income is at least 2x monthly rent.
              </Paragraph>
            </Layout.Content>
            <Layout.Content
              css={gridContentStyles}
              data-testid="RentalProtectionHowDoesItWorkModalBodyItem-LowFriction"
            >
              <Image src={SetupPaymentsImage} alt="" css={{ width: '190px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 3
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Pay for your policy
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Sign the request for Rental Protection and share payment details. We&apos;ll auto-charge your premium
                every month.
              </Paragraph>
            </Layout.Content>
          </>
        ) : (
          <>
            <Layout.Content css={gridContentStyles} data-testid="RentalProtectionHowDoesItWorkModalBodyItem">
              <Image src={ChooseARenterImage} alt="" css={{ width: '190px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 1
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Accept Zillow Applications
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Select the renter who&apos;s right for you and simplify income verification.
              </Paragraph>
            </Layout.Content>
            <Layout.Content css={gridContentStyles} data-testid="RentalProtectionHowDoesItWorkModalBodyItem">
              <Image src={AddALeaseImage} alt="" css={{ width: '150px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 2
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Upload or create a Zillow Lease{' '}
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Upload your own 6 - 24 month lease or use our template to create a new one.
              </Paragraph>
            </Layout.Content>
            <Layout.Content css={gridContentStyles} data-testid="RentalProtectionHowDoesItWorkModalBodyItem">
              <Image src={SetupPaymentsImage} alt="" css={{ width: '190px', height: '190px' }} />
              <Box css={{ marginBlock: '400' }}>
                <Paragraph css={{ textAlign: 'left' }} textStyle="body-sm">
                  Step 3
                </Paragraph>
                <Paragraph textStyle="body-lg-bold" css={{ textAlign: 'left' }}>
                  Set up Zillow Payments{' '}
                </Paragraph>
              </Box>
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ textAlign: 'left', marginBottom: '400' }}>
                Simplify missed rent claims by tracking payment history. Payments is free for both you and your renter.
              </Paragraph>
            </Layout.Content>
          </>
        )}
      </Layout.Root>
      {!isFromModularListingFlow && renderAccordion(isLowFrictionEnabled)}
      {onDropRentalProtectionClick && (
        <Box css={{ marginTop: '800' }}>
          <Text>
            No longer interested?{' '}
            <TextButton onClick={onDropRentalProtectionClick}>Drop Rental Protection request</TextButton>
          </Text>
        </Box>
      )}
    </div>
  );
};

export default RentalProtectionHowDoesItWorkModalBody;
